import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { default as OriginalCountdown, CountdownProps as OriginalCountdownProps } from "react-countdown";

type CountdownProps = Omit<OriginalCountdownProps, "ref">;

const maxStartRetries = 5;

const Countdown = ({ date, ...rest }: CountdownProps) => {
    const ref = useRef<OriginalCountdown>(null);
    const retryTimeout = useRef<ReturnType<typeof setTimeout>>();

    const internalDate = useMemo(() => (date ? new Date(date) : null), [date]);

    const restart = useCallback((retryCount = 0) => {
        try {
            ref.current?.start();
        } catch (e) {
            console.log(e);
            // Bounded (as on mobile): this used to pass retryCount++ (the old value), so it retried every 100ms forever.
            if (retryCount < maxStartRetries) {
                retryTimeout.current = setTimeout(() => restart(retryCount + 1), 100);
                console.log("Countdown=> Attempting to start timer");
            }
        }
    }, []);

    useEffect(() => {
        if (internalDate) {
            restart();
        }
        return () => clearTimeout(retryTimeout.current);
    }, [internalDate, restart]);

    // The display has one-second precision, so tick once a second (was every 500ms, i.e. two renders a second per card).
    return internalDate ? <OriginalCountdown intervalDelay={1000} precision={1} date={internalDate} {...rest} ref={ref} /> : null;
};

export default Countdown;
